import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getFunctions, httpsCallable } from 'firebase/functions';
import mixpanel from 'mixpanel-browser';
import { filter, combineLatest, Subject, switchMap, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { Navigation, Pagination, SwiperOptions } from 'swiper';
import { IToolbarData } from '../../shared/models/components-data.models';
import { AuthService } from '../../firebase/auth.service';
import { PlanetStore } from '../planet-store';
import { getTimeUntilNextSunday, successPlanetModalData } from '../planet';
import { environment } from '../../../environments/environment';
import { CrudHelper } from '../../firebase/helpers/crudHelper';
import { UserService } from 'src/app/auth/user.service';
import { PopoverController, ToastController } from '@ionic/angular';
import { ShareOptionsComponent } from '../../activity-feed/post-details/share-options/share-options.component';
import { ReactionsService } from '../../services/reactions.service';

const TOOLBAR_DATA: IToolbarData = {
  arrowRouter: 'back',
  btnSlot: 'end',
  title: 'Planet',
};

const TOOLBAR_SWIPER: IToolbarData = {
  arrowRouter: '',
  btnSlot: 'end',
  title: 'Daily Challenge Pictures',
};

@Component({
  selector: 'app-planet-details',
  templateUrl: './planet-details.component.html',
  styleUrls: ['./planet-details.component.scss'],
})
export class PlanetDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('col') col: ElementRef;
  isOpenFlagModal: boolean = false;
  galleryUrls: string[];
  isImgLoaded: boolean = false;
  config: SwiperOptions;
  TOOLBAR_DATA = TOOLBAR_DATA;
  TOOLBAR_SWIPER = TOOLBAR_SWIPER;
  currentUser;
  currentUserID;
  isContentFlaggedModal: boolean = false;
  planetItem;
  experiences;
  items;
  creator;
  showAddExperienceModal;
  isOpen = false;
  showSuccessModal = false;
  successModalData = successPlanetModalData;
  isOpenFormModal = false;
  isOpenAddModal;
  isOpenEditModal;
  id = this.activeRouter.snapshot.params.id;
  showStatusModal: boolean = false;
  itemStatus;
  timer;
  properties;
  flagData;

  isTodaysChallenge = false;
  private destroy$ = new Subject<void>();
  hasLiked = false;
  likesCount = 0;
  likesInfo;
  loadingLikeButton = false;

  constructor(
    private planetStore: PlanetStore,
    private activeRouter: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private crudHelper: CrudHelper,
    private popoverController: PopoverController,
    private toastController: ToastController,
    private reactionsService: ReactionsService
  ) {
    setInterval(() => {
      let time = getTimeUntilNextSunday();
      this.timer = time;
    }, 1000);
  }

  ngOnInit() {
    mixpanel.init(environment.mixpanelToken, { debug: true });
    const { uid } = this.authService.isUserAuth();
    mixpanel.identify(uid);
    this.getUser();

    if (this.id) {
      this.planetStore.getItemByByValue({
        documentId: this.id,
      });
      this.listenOlderPlanetDetails();
    } else {
      this.planetStore.getItems({});
      this.listenTodayPlanetDetails();
    }

    this.planetStore.selectShowFormModal().subscribe(isOpen => {
      this.isOpenFormModal = isOpen;
    });

    this.planetStore.selectShowAddFormModal().subscribe(isOpen => {
      this.isOpenAddModal = isOpen;
    });

    this.planetStore.selectShowEditFormModal().subscribe(isOpen => {
      this.isOpenEditModal = isOpen;
    });

    this.planetStore.selectShowPendingModal().subscribe(isOpen => {
      this.showSuccessModal = isOpen;
    });

    // Subscribe to reactions
    this.reactionsService
      .getReactions()
      .pipe(
        takeUntil(this.destroy$),
        map(
          (items: any) =>
            items && items.filter(item => !!item && item.id === this.id)
        )
      )
      .subscribe((data: any[]) => {
        this.likesInfo = data[0]?.likes;
        this.likesCount = this.likesInfo?.length || 0;
        this.hasLiked = this.likesInfo?.includes(this.currentUserID) || false;
        this.loadingLikeButton = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.planetItem = null;
    this.id = null;
  }

  trackEvent(event, parameter, parameterValue) {
    if (parameter === 'screen-name') {
      this.properties = {
        'screen-name': parameterValue,
      };
    }

    mixpanel.track(event, this.properties);
  }

  openStatusModal(status?) {
    this.showStatusModal = !this.showStatusModal;
    if (status) this.itemStatus = status;
  }

  toggleGallery(i?) {
    this.isOpen = !this.isOpen;
    this.config = {
      modules: [Pagination, Navigation],
      loop: true,
      pagination: { clickable: true, dynamicBullets: true },
      initialSlide: i,
      injectStyles: [
        `
          .swiper-pagination-bullet {
            width:14px;
            height:14px
          }`,
      ],
    };
  }
  handleOk(e) {
    e ? this.planetStore.updateShowPendingModal(false) : null;
  }

  handleCancel(e) {
    e ? this.planetStore.updateShowPendingModal(false) : null;
  }

  openModal() {
    this.planetStore.updateShowFormModal(true);
  }

  closeModal(e) {
    e ? this.planetStore.updateShowFormModal(false) : null;
  }

  openAddModal() {
    this.planetStore.updateShowAddFormModal(true);
  }

  closeAddModal(e) {
    e ? this.planetStore.updateShowAddFormModal(false) : null;
  }

  openEditModal() {
    this.planetStore.updateShowEditFormModal(true);
  }

  closeEditModal(e) {
    e ? this.planetStore.updateShowEditFormModal(false) : null;
  }

  openAddExperienceModal() {
    this.showAddExperienceModal = true;
  }

  navToProfile(item) {
    this.isOpen = false;
    this.router.navigate(['/profile/', item.creator.id]);
  }

  private listenOlderPlanetDetails() {
    this.planetStore
      .selectItem()
      .pipe(
        takeUntil(this.destroy$),
        filter(item => !!item),
        switchMap(item => {
          if (this.id) {
            this.planetItem = item;
            this.experiences = item?.experiences.filter(
              item => !this.currentUser?.blockList?.includes(item?.creator?.uid)
            );
          }
          this.isTodaysChallenge = false;
          if (this.experiences.length >= 1) {
            let experiencesImages = [];
            this.experiences.forEach(experience => {
              experiencesImages.push(experience.image);
            });
            const participants$ = this.experiences.map(item =>
              this.authService.getUserByUid(item.creator.id)
            );

            combineLatest(participants$)
              .pipe(filter(part => !!part))
              .subscribe((participants: any[]) => {
                this.experiences.map(item => {
                  const newCreator = participants.find(
                    participant => participant.id === item.creator.id
                  );

                  newCreator ? (item.creator = newCreator) : 'null';
                });
              });
            this.galleryUrls = experiencesImages.map(pic => pic);
          }
          const participants$ = this.experiences.map(item =>
            this.authService.getUserByUid(item.creator.id)
          );

          combineLatest(participants$)
            .pipe(filter(part => !!part))
            .subscribe((participants: any[]) => {
              this.experiences.map(item => {
                const newCreator = participants.find(
                  participant => participant.id === item.creator.id
                );

                newCreator ? (item.creator = newCreator) : 'null';
              });
            });
          return this.authService.getUserByUid(item.creator.id);
        })
      )
      .subscribe(user => {
        this.creator = user;
      });
  }

  private listenTodayPlanetDetails() {
    combineLatest([this.planetStore.selectItems()])
      .pipe(
        takeUntil(this.destroy$),
        filter(items => !!items),
        map(
          ([items]) =>
            items &&
            (items.find(item => {
              let actualDate = new Date();
              actualDate.setHours(3);
              actualDate.setMinutes(0);
              actualDate.setSeconds(0);
              if (item.challengeDate) {
                return (
                  item.challengeDate.seconds ===
                  Math.floor(actualDate.getTime() / 1000)
                );
              }
            }) ||
              items.find(item => item.challengeDate !== null))
        )
      )
      .subscribe(item => {
        if (!this.id) {
          this.planetItem = item;
          this.experiences = item?.experiences.filter(
            item => !this.currentUser.blockList?.includes(item.creator.uid)
          );
          this.isTodaysChallenge = true;
          // this.planetStore.getItemByByValue({
          //   documentId: item.id,
          // });
        }
        if (this.experiences?.length >= 1) {
          let experiencesImages = [];
          this.experiences.forEach(experience => {
            experiencesImages.push(experience.image);
          });

          const participants$ = this.experiences.map(item =>
            this.authService.getUserByUid(item.creator.id)
          );

          combineLatest(participants$)
            .pipe(filter(part => !!part))
            .subscribe((participants: any[]) => {
              this.experiences.map(item => {
                const newCreator = participants.find(
                  participant => participant.id === item.creator.id
                );

                newCreator ? (item.creator = newCreator) : 'null';
              });
            });

          this.galleryUrls = experiencesImages.map(pic => pic);
        }
      });
  }

  async flagContent(slide, type) {
    slide.itemId = this.planetItem.id;
    if (type === 'challengeExperience') {
      slide.type = 'challengeExperience';
    } else if (type === 'planet') {
      slide.type = 'planet';
    }

    this.flagData = slide;
    const functions = getFunctions();
    const isContentFlagged = httpsCallable(functions, 'isContentFlagged');
    const res = await isContentFlagged({
      itemId: slide.itemId,
      url: this.flagData.image,
      type: this.flagData.type,
    });
    if (res.data === true) {
      this.isContentFlaggedModal = true;
    } else {
      this.isOpenFlagModal = true;
    }
  }

  private getUser() {
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid)
      )
      .subscribe(user => {
        this.currentUser = user;
        this.currentUserID = user.id;
      });
  }

  onImgLoad() {
    this.isImgLoaded = true;
  }

  async toggleLike() {
    if (this.loadingLikeButton) return;
    this.loadingLikeButton = true;

    try {
      const functions = getFunctions();
      const toggleLike = httpsCallable(functions, 'toggleLike');
      await toggleLike({
        itemId: this.id,
        itemCollection: 'planet',
        userId: this.currentUserID,
      });
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }

  private async copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      const toast = await this.toastController.create({
        message: 'Link copied to clipboard!',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }

  async sharePost(event: any) {
    const postUrl = window.location.href;
    const popover = await this.popoverController.create({
      component: ShareOptionsComponent,
      componentProps: {
        postUrl: postUrl,
        postTitle: 'MerMapp Planet Challenge',
        postText: `Check out this planet challenge on MerMapp: ${this.planetItem?.title}`,
        onOptionSelected: async (option: string) => {
          await this.handleShareOption(option, postUrl);
          popover.dismiss();
        },
      },
      event: event,
      translucent: true,
      backdropDismiss: true,
    });

    await popover.present();
  }

  private async handleShareOption(option: string, postUrl: string) {
    const title = 'MerMapp Planet Challenge';
    const text = `Check out this planet challenge on MerMapp: ${this.planetItem?.title}`;

    switch (option) {
      case 'copy':
        await this.copyToClipboard(postUrl);
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            postUrl
          )}`,
          '_blank'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            postUrl
          )}&text=${encodeURIComponent(text)}`,
          '_blank'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text + ' ' + postUrl)}`,
          '_blank'
        );
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(
          title
        )}&body=${encodeURIComponent(text + '\n\n' + postUrl)}`;
        break;
      case 'share':
      case 'native':
        await this.useNativeShare(postUrl, title, text);
        break;
    }
  }

  private async useNativeShare(
    postUrl: string,
    title: string = 'MerMapp Planet Challenge',
    text: string = 'Check out this planet challenge on MerMapp!'
  ) {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: postUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          await this.copyToClipboard(postUrl);
        }
      }
    } else {
      await this.copyToClipboard(postUrl);
    }
  }
}

